<template>
  <div>
    <div class="row pt-3 l-m-b-20">
      <div class="col-9">
        <h1>Trucks</h1>
      </div>
      <div class="col-3 text-right" v-if="$_can('carriers.trucks.store')">
        <button class="btn btn-primary" @click="showCreateSidebar = !showCreateSidebar">
          ADD TRUCK
        </button>
      </div>
    </div>
    <div class="row">
      <div class="col-12 l-m-b-20">
        <div class="input-group search">
          <button type="button" class="btn input-group-btn" @click="clearSearch">
            <i :class="[q ? 'fa fa-remove' : 'fa fa-search']" />
          </button>
          <input class="form-control" type="text" v-model="q" @keyup="searchText($event)" placeholder="Search" />
        </div>
      </div>
      <div class="col-12 filters-field">
        <span>Filters:</span>
        <span v-if="filtersLoaded">
          <span v-if="filters.carrier.length && carriers">
            <span v-for="carrier in filters.carrier" :key="carrier" class="filter-item btn btn-filter">
              <span class="mod-icon-filter" @click="removeFilter('carrier', carrier)" />
              Carrier: {{ carriers.find(el => el.id === carrier).name }}</span>
          </span>
          <span v-if="filters.status" class="filter-item btn btn-filter">
            <span class="mod-icon-filter" @click="removeFilter('status')" />
            Status: {{ truckStatuses.find(el => el.id === filters.status).name }}</span>
        </span>
        <button class="btn btn-secondary add-filters" @click="showFilters = true">
          <i class="fa fa-plus filter-icon" />
        </button>
      </div>
    </div>
    <template v-if="!trucks">
      <vue-content-loading :width="800" :height="450">
        <rect v-for="x in 15" :key="x" x="10" :y="15*x*1.4" rx="5" ry="5" width="720" height="6" />
      </vue-content-loading>
    </template>
    <template v-else>
      <table class="table m-table table-hover l-m-t-20">
        <thead class="m-table__heading">
          <tr>
            <th class="m-table__id">ID</th>
            <th @click="sortBy('name')" :class="{ active: sortKey === 'name' }" class="sortable">
              Name
              <span class="arrow" :class="sortOrders['name'] ? 'dsc' : 'asc'" />
            </th>
            <th @click="sortBy('licence_plate')" :class="{ active: sortKey === 'licence_plate' }"
                class="sortable"
            >
              Licence plate
              <span class="arrow" :class="sortOrders['licence_plate'] ? 'dsc' : 'asc'" />
            </th>
            <th @click="sortBy('truck_size_id')" :class="{ active: sortKey === 'truck_size_id' }"
                class="sortable"
            >
              Truck size
              <span class="arrow" :class="sortOrders['truck_size_id'] ? 'dsc' : 'asc'" />
            </th>
            <th @click="sortBy('make')" :class="{ active: sortKey === 'make' }" class="sortable">
              Make
              <span class="arrow" :class="sortOrders['make'] ? 'dsc' : 'asc'" />
            </th>
            <th @click="sortBy('model')" :class="{ active: sortKey === 'model' }" class="sortable">
              Model
              <span class="arrow" :class="sortOrders['model'] ? 'dsc' : 'asc'" />
            </th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="truck in trucks" @mouseup="$_can('carriers.trucks.show') && view($event, truck.id)" class="pointer"
              :key="truck.id"
          >
            <td>{{ truck.id }}</td>
            <td>{{ truck.name }}</td>
            <td>{{ truck.licence_plate }}</td>
            <td>{{ truck.truck_size.name }}</td>
            <td>{{ truck.make }}</td>
            <td>{{ truck.model }}</td>
            <td>{{ truck.status.name }}</td>
          </tr>
          <tr v-if="trucks && !trucks.length">
            <td colspan="7" class="text-center">
              No results found
            </td>
          </tr>
        </tbody>
      </table>

      <div class="row">
        <div class="col-sm-6 mb-1 text-black-50"
             v-if="meta.total"
        >
          Showing {{ meta.from }} - {{ meta.to }} of {{ meta.total }}
        </div>
        <pagination class="col-sm-6 mb-1" :change-page="changePage" :meta="meta" />
      </div>
    </template>

    <!-- Filters -->
    <transition name="slide-fade">
      <div class="l-modal-mask" v-if="showFilters" @click.self="showFilters = false">
        <div v-if="showFilters" class="filters-side-modal flex flex-column">
          <div @click="showFilters = !showFilters" class="pointer">
            <h1 class="modal-h1 l-m-b-20"><span class="btn btn-back" />Add Filter</h1>
          </div>
          <div>
            <p>
              Filters are applied automatically. You can remove them one by one with minus button or with clear filters button.
            </p>
          </div>
          <div class="l-bg-white">
            <div class="row">
              <div class="col">
                <div class="row filters">
                  <div class="col-12" v-if="carriers && carriers.length">
                    <div class="select-wrap">
                      <label>Carrier</label>
                      <key-multiselect v-model="filters.carrier"
                                       :options="carriers"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :close-on-select="false"
                                       :hide-selected="true"
                                       :id="'carrier-select'"
                                       :placeholder="'Select Carriers'"
                                       :preserve-search="false"
                                       label="name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="select-wrap">
                      <label>Status</label>
                      <simple-select v-model="filters.status"
                                     :options="truckStatuses"
                                     placeholder="Select Status"
                                     @input="search"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-auto">
            <div class="col">
              <button type="button" class="btn btn-not-active text-dark w-100" @click="clearFilters"
                      v-if="!(!filters.carrier.length && !filters.status)"
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Create Truck -->
    <transition name="slide-fade">
      <div class="l-modal-mask" v-if="showCreateSidebar" @click.self="showCreateSidebar = false">
        <trucks-create class="sidebar-right" @cancel="showCreateSidebar = false" @update="getTrucks" />
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
import Pagination from '../Pagination';
import TrucksCreate from './TrucksCreate';

export default {
  name: 'Trucks',
  components: { TrucksCreate, Pagination },
  data: () => ({
    filters: {
      carrier: [],
      status: '',
    },
    sortKey: '',
    sortOrders: [],
    page: 1,
    q: '',
    showCreateSidebar: false,
    showFilters: false,
  }),
  computed: {
    ...mapState({
      trucks: state => state.trucks.trucks.data,
      meta: state => state.trucks.trucks.meta,
      carriers: state => state.carriers.carriers.data,
      truckStatuses: state => state.values.truckStatuses,
      filtersSaved: state => state.filters.filtersSaved,
      filtersLoaded: state => state.guard.filtersLoaded,
    }),
    ...mapGetters(['getTrucksFiltered', 'getFiltersSaved']),
  },
  mounted() {
    this.getTruckStatuses();
    if (this.filtersSaved && this.filtersSaved.previousPage === 'TrucksEdit') {
      this.filters = { ...this.filtersSaved };
      this.page = this.filtersSaved.page;
      this.filter();
    } else {
      if (Object.keys(this.$route.query).length !== 0) {
        this.setFilters(this.$route.query);
      }
      this.filter();
    }
  },
  methods: {
    ...mapActions(['getTrucks', 'getTruckStatuses', 'removeTruck']),
    ...mapMutations(['setTrucks', 'setFiltersSaved']),
    setFilters(query) {
      this.filters = Object.assign(
        {
          carrier: query.carrier_id ? query.carrier_id.split(',').map(i => Number(i)) : [],
          status: query.status_id ? parseInt(query.status_id, 10) : '',
          sortKey: query.sort,
          sortOrders: query.order,
        },
        {},
      );
      this.page = query.page;
      this.q = query.q.toString();
    },
    view(event, id) {
      if (event.button === 1) {
        window.open(this.$router.resolve(`/trucks/edit/${id}`).href, '_blank');
      } else {
        this.$router.push(`/trucks/edit/${id}`);
      }
    },
    changePage(pageNum) {
      this.setTrucks({});
      this.page = pageNum;
      this.filter();
    },
    searchText(e) {
      this.page = 1;
      if (/^[a-zA-Z0-9]$/.test(e.key) || e.keyCode === 8 || e.keyCode === 13) {
        const vm = this;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          vm.filter();
        }, 500);
      }
    },
    search() {
      this.page = 1;
      this.filter();
    },
    filter() {
      const query =
        `page=${this.page}&carrier_id=${this.filters.carrier}&status_id=${
          this.filters.status
        }&q=${encodeURIComponent(this.q)}` +
        `&sort=${this.sortKey}&order=${this.sortOrders[this.sortKey] ? 'asc' : 'desc'}`;
      this.getTrucksFiltered(query);
      if (this.$_checkProperties(this.filters)) {
        this.$router.push(`/trucks?${query}`);
      } else {
        this.$router.push(`/trucks`);
      }
    },
    clearFilters() {
      this.filters = Object.assign(
        {
          carrier: [],
          status: '',
        },
        {},
      );
      this.sortKey = '';
      this.sortOrders = [];
      this.filter();
      this.showFilters = false;
    },
    clearSearch() {
      this.q = '';
      this.filter();
    },
    sortBy(key) {
      this.sortKey = key;
      this.$set(this.sortOrders, key, !this.sortOrders[key]);
      this.filter();
    },
    removeFilter(model, id) {
      if (id) {
        this.filters[model] = this.filters[model].filter(element => element !== id);
      } else {
        this.filters[model] = '';
      }
      this.search();
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/trucks/Trucks.vue